/**
 * LoadingScreen Component
 * =======================
 * Phase 1: Fullscreen loading with logo, glow pulse, progress ring
 * Phase 2: Transition with ring expansion and particle dissolve
 */

import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import ProgressRing from './ProgressRing';

// Import the logo - adjust path based on your setup
import logo from '../../assets/dravonix_icon.png';

const LoadingScreen = ({ phase, onPhaseComplete, registerTimeline }) => {
  const containerRef = useRef(null);
  const logoRef = useRef(null);
  const glowRef = useRef(null);
  const textRef = useRef(null);
  const ringRef = useRef(null);
  const particlesRef = useRef(null);
  const timelineRef = useRef(null);

  // Loading Phase Animation
  useEffect(() => {
    if (phase !== 'loading') return;

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        onComplete: () => onPhaseComplete('loading'),
      });

      // Register timeline for skip functionality
      registerTimeline(tl);
      timelineRef.current = tl;

      // Initial states
      gsap.set([logoRef.current, textRef.current], { opacity: 0 });
      gsap.set(logoRef.current, { scale: 0.8 });

      // Logo fade in and scale
      tl.to(logoRef.current, {
        opacity: 1,
        scale: 1,
        duration: 0.6,
        ease: 'power2.out',
      });

      // Start glow pulse (continuous)
      tl.to(
        glowRef.current,
        {
          opacity: 0.8,
          scale: 1.2,
          duration: 0.8,
          ease: 'power1.inOut',
          repeat: 3,
          yoyo: true,
        },
        '-=0.3'
      );

      // Text typing effect
      tl.to(
        textRef.current,
        {
          opacity: 1,
          duration: 0.4,
          ease: 'power1.out',
        },
        '-=2.4'
      );

      // Hold for visual impact
      tl.to({}, { duration: 0.5 });
    }, containerRef);

    return () => ctx.revert();
  }, [phase, onPhaseComplete, registerTimeline]);

  // Transition Phase Animation
  useEffect(() => {
    if (phase !== 'transition') return;

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        onComplete: () => onPhaseComplete('transition'),
      });

      // Create particle dissolve effect
      createDissolveParticles();

      // Ring expansion (shockwave)
      tl.to(ringRef.current, {
        scale: 15,
        opacity: 0,
        duration: 1,
        ease: 'power2.out',
      });

      // Logo dissolve
      tl.to(
        logoRef.current,
        {
          opacity: 0,
          scale: 1.1,
          filter: 'blur(10px)',
          duration: 0.6,
          ease: 'power2.in',
        },
        '-=0.8'
      );

      // Text dissolve
      tl.to(
        textRef.current,
        {
          opacity: 0,
          y: -20,
          duration: 0.4,
          ease: 'power2.in',
        },
        '-=0.6'
      );

      // Particles scatter
      tl.to(
        '.dissolve-particle',
        {
          x: () => gsap.utils.random(-300, 300),
          y: () => gsap.utils.random(-300, 300),
          opacity: 0,
          scale: 0,
          duration: 0.8,
          stagger: 0.02,
          ease: 'power2.out',
        },
        '-=0.4'
      );

      // Container fade out
      tl.to(
        containerRef.current,
        {
          opacity: 0,
          duration: 0.3,
          ease: 'power1.out',
        },
        '-=0.2'
      );
    }, containerRef);

    return () => ctx.revert();
  }, [phase, onPhaseComplete]);

  // Create dissolve particles
  const createDissolveParticles = () => {
    if (!particlesRef.current) return;

    const particleCount = window.innerWidth < 768 ? 20 : 40;
    particlesRef.current.innerHTML = '';

    for (let i = 0; i < particleCount; i++) {
      const particle = document.createElement('div');
      particle.className = 'dissolve-particle';
      particle.style.left = `${50 + gsap.utils.random(-20, 20)}%`;
      particle.style.top = `${50 + gsap.utils.random(-20, 20)}%`;
      particle.style.width = `${gsap.utils.random(4, 12)}px`;
      particle.style.height = particle.style.width;
      particle.style.animationDelay = `${gsap.utils.random(0, 0.5)}s`;
      particlesRef.current.appendChild(particle);
    }
  };

  return (
    <div className="loading-screen" ref={containerRef}>
      {/* Ambient glow background */}
      <div className="loading-ambient"></div>

      {/* Glow effect behind logo */}
      <div className="logo-glow" ref={glowRef}></div>

      {/* Progress Ring */}
      <div className="progress-ring-container" ref={ringRef}>
        <ProgressRing isAnimating={phase === 'loading'} />
      </div>

      {/* Dragon Logo */}
      <div className="loading-logo" ref={logoRef}>
        <img
          src={logo}
          alt="Dravonix"
          className="loading-logo-image"
        />
      </div>

      {/* Loading Text */}
      <div className="loading-text" ref={textRef}>
        <span className="loading-text-content">Initializing Dravonix Systems</span>
        <span className="loading-dots">
          <span>.</span>
          <span>.</span>
          <span>.</span>
        </span>
      </div>

      {/* Dissolve Particles Container */}
      <div className="dissolve-particles" ref={particlesRef}></div>
    </div>
  );
};

export default LoadingScreen;
